/**
 * <mineralia-restock data-variant-id="123" [data-customer-email="a@b.com"]>
 *
 * "Avísame cuando vuelva" built on the native Shopify contact form.
 * - Sends the form with fetch; success = final URL contains `contact_posted=true`.
 * - If Shopify answers with a challenge (captcha), it falls back to a regular submit.
 * - Remembers the email and the subscribed variant ids in localStorage so a returning visitor
 *   can subscribe with one click and sees a confirmation for variants already requested.
 *
 * It never promises an automatic email: requests reach the shop inbox as contact messages.
 */

const EMAIL_KEY = 'mineralia-restock-email';
const VARIANTS_KEY = 'mineralia-restock-variants';
const PENDING_KEY = 'mineralia-restock-pending';
const MAX_VARIANTS = 60;

const storage = {
  get(key) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      /* storage unavailable (private mode / quota) */
    }
  },
};

function getVariants() {
  try {
    const value = JSON.parse(storage.get(VARIANTS_KEY) || '[]');
    return Array.isArray(value) ? value.map(String) : [];
  } catch {
    return [];
  }
}

function rememberVariant(variantId, email) {
  const variants = getVariants().filter((id) => id !== variantId);
  variants.unshift(variantId);
  storage.set(VARIANTS_KEY, JSON.stringify(variants.slice(0, MAX_VARIANTS)));
  if (email) storage.set(EMAIL_KEY, email);
}

class MineraliaRestock extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {HTMLInputElement | null} */
  #input = null;
  #busy = false;

  connectedCallback() {
    this.#form = this.querySelector('form');
    this.#input = this.querySelector('[data-restock-email]');
    if (!this.#form || !this.#input) return;

    this.#form.addEventListener('submit', this.#onSubmit);
    this.addEventListener('click', this.#onClick);

    this.#resolvePendingNativeSubmit();
    this.#render();
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('click', this.#onClick);
  }

  get variantId() {
    return String(this.dataset.variantId || '');
  }

  get knownEmail() {
    return storage.get(EMAIL_KEY) || this.dataset.customerEmail || '';
  }

  /** After a native (fallback) submit, Shopify reloads the page with `contact_posted=true`. */
  #resolvePendingNativeSubmit() {
    let pending = null;
    try {
      pending = JSON.parse(window.sessionStorage.getItem(PENDING_KEY) || 'null');
    } catch {
      pending = null;
    }
    if (!pending || pending.variantId !== this.variantId) return;

    try {
      window.sessionStorage.removeItem(PENDING_KEY);
    } catch {
      /* ignore */
    }

    if (this.querySelector('[data-restock-server-success]')) {
      rememberVariant(this.variantId, pending.email);
    }
  }

  /** @param {'form' | 'known' | 'done'} [forced] */
  #render(forced) {
    const email = this.knownEmail;
    let state = forced;
    if (!state) {
      if (email && getVariants().includes(this.variantId)) state = 'done';
      else if (email) state = 'known';
      else state = 'form';
    }

    for (const panel of this.querySelectorAll('[data-restock-state]')) {
      panel.hidden = panel.getAttribute('data-restock-state') !== state;
    }
    for (const label of this.querySelectorAll('[data-restock-email-label]')) {
      label.textContent = email;
    }

    const note = this.querySelector('[data-restock-note]');
    if (note instanceof HTMLElement) note.hidden = state === 'done';

    if (this.#input) {
      // Hidden "form" panel still holds the input; keep it valid for one-click subscribe.
      if (state !== 'form' && email) this.#input.value = email;
      else if (state === 'form' && forced === 'form') this.#input.value = '';
    }

    this.dataset.state = state;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-restock-change]') : null;
    if (!target) return;
    event.preventDefault();
    this.#setStatus('');
    this.#render('form');
    this.#input?.focus();
  };

  /** @param {string} message @param {'error' | 'info'} [tone] */
  #setStatus(message, tone = 'info') {
    const status = this.querySelector('[data-restock-status]');
    if (!(status instanceof HTMLElement)) return;
    status.textContent = message;
    status.dataset.tone = tone;
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    for (const button of this.querySelectorAll('[data-restock-submit]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.disabled = busy;
      button.setAttribute('aria-busy', String(busy));
    }
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const form = this.#form;
    const input = this.#input;
    if (!form || !input) return;

    event.preventDefault();
    if (this.#busy) return;

    const email = input.value.trim();
    if (!email || !input.checkValidity()) {
      input.setAttribute('aria-invalid', 'true');
      this.#render('form');
      this.#setStatus('Escribe un correo válido, por ejemplo nombre@correo.com.', 'error');
      input.focus();
      return;
    }

    input.removeAttribute('aria-invalid');
    this.#setBusy(true);
    this.#setStatus('Enviando…');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });
      const finalUrl = response.url || '';

      if (finalUrl.includes('/challenge')) {
        this.#nativeSubmit(email);
        return;
      }

      if (response.ok && finalUrl.includes('contact_posted=true')) {
        rememberVariant(this.variantId, email);
        this.#setStatus('');
        this.#render('done');
        return;
      }

      throw new Error(`Unexpected response ${response.status}`);
    } catch (error) {
      console.warn('[mineralia-restock]', error);
      this.#render('form');
      input.value = email;
      this.#setStatus('No pudimos registrar tu correo. Inténtalo de nuevo en un momento.', 'error');
    } finally {
      this.#setBusy(false);
    }
  };

  /** @param {string} email */
  #nativeSubmit(email) {
    try {
      window.sessionStorage.setItem(PENDING_KEY, JSON.stringify({ variantId: this.variantId, email }));
    } catch {
      /* ignore */
    }
    storage.set(EMAIL_KEY, email);
    this.#form?.removeEventListener('submit', this.#onSubmit);
    HTMLFormElement.prototype.submit.call(this.#form);
  }
}

if (!customElements.get('mineralia-restock')) {
  customElements.define('mineralia-restock', MineraliaRestock);
}
